import {
  ComponentWithAs,
  Box,
  BoxProps,
  SkeletonProps,
  SimpleGridProps,
  Button,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useMultiStyleConfig
} from '~/design_system'

import { HOME_STATS_QUERY_KEY, useHomeStatsQuery } from '~/api/home'
import { queryClient } from '~/common/react-query/queryClient'
import { StatCard } from '~/components/v2/homepage/StatCard'
import { Briefcase, Person, Rfp, Sow, Transfer, UserCards, WorkerTracking, WorkOrders } from '~/icons'
import { iconColor } from '~/home/BannerGraphic'

const icons: Record<string, ComponentWithAs<'div', BoxProps>> = {
  jobs: Briefcase,
  work_orders: WorkOrders,
  contractors: UserCards,
  wo_mods: Transfer,
  ic: Person,
  sow: Sow,
  worker_trackers: WorkerTracking,
  rfp: Rfp
}

export const HomeStatCards = (props: SimpleGridProps) => {
  const { data, isLoading, isError } = useHomeStatsQuery()
  const headerItems = isLoading ? getSkeletonData() : data?.header_items
  const numItems = headerItems?.length ?? 0

  const numItemsToColumns: Record<string, number> = { '1': 2, '2': 2, '3': 3, '4': 2, '5': 3, '6': 3, '7': 4, '8': 4 }

  if (numItems > 0) {
    return (
      <SimpleGrid
        spacing={6}
        columns={{
          base: 1,
          sm: 2,
          md: numItemsToColumns[String(numItems)] ?? 2
        }}
        role="region"
        aria-label="Program Health"
        {...props}
        {...(isLoading && {
          h: '138px',
          overflowY: 'hidden'
        })}>
        {headerItems?.map(
          ({ id, title, content, url, sub_header1, sub_content1, sub_url1, sub_header2, sub_content2, sub_url2 }) => {
            const icon = <StatCard.Icon color={iconColor} icon={icons[id]} />
            const mainStat = <StatCard.MainStat data-testid={title} label={title} stat={content} href={url} />
            const subStats = (
              <>
                <StatCard.SubStat
                  data-testid={`${title}-${sub_header1}`}
                  ariaPrefix={title}
                  label={sub_header1}
                  stat={sub_content1}
                  href={sub_url1}
                />
                <StatCard.SubStat
                  data-testid={`${title}-${sub_header2}`}
                  ariaPrefix={title}
                  label={sub_header2}
                  stat={sub_content2}
                  href={sub_url2}
                />
              </>
            )
            return (
              <StatCard
                data-testid={`stat-card-${title}`}
                key={id}
                icon={icon}
                mainStat={mainStat}
                subStats={subStats}
                h="full"
              />
            )
          }
        )}
      </SimpleGrid>
    )
  }

  return !isLoading && isError ? <StatsError {...props} /> : null
}

const StatsError = (props: BoxProps) => {
  const styles = useMultiStyleConfig('Card')
  // Box + __css, not Card. Card's narrowed `variant` typing fights parent BoxProps.
  return (
    <Box __css={styles.container} role="region" data-card px={4} py={6} {...props}>
      <Stack alignItems="center">
        <Text textStyle="bodySemi">Content failed to load</Text>
        <Text textStyle="body" flex="1">
          If the problem persists, contact customer support.
        </Text>
        <Button variant="inlineLink" onClick={() => queryClient.invalidateQueries([HOME_STATS_QUERY_KEY])}>
          Refresh
        </Button>
      </Stack>
    </Box>
  )
}

function getSkeletonData() {
  const skeletonItem = (id: string) => ({
    id,
    title: <TransparentSkeleton w="96px" h={5} mb={1} />,
    content: <TransparentSkeleton w="48px" h={8} />,
    sub_header1: <TransparentSkeleton h={4} w={3 / 4} />,
    sub_content1: <TransparentSkeleton h={5} w={5} mt={1} />,
    sub_header2: <TransparentSkeleton h={4} w={3 / 4} />,
    sub_content2: <TransparentSkeleton h={5} w={5} mt={1} />,
    url: undefined,
    sub_url1: undefined,
    sub_url2: undefined
  })
  return [skeletonItem('-4'), skeletonItem('-3'), skeletonItem('-2'), skeletonItem('-1')]
}

function TransparentSkeleton(props: SkeletonProps) {
  return (
    <Skeleton
      as="span"
      display="block"
      startColor="rgba(255,255,255,0.1)"
      endColor="rgba(255,255,255,0.4)"
      {...props}
    />
  )
}
